import React, { CSSProperties, Fragment, useRef, useState } from "react";
import { format, register } from "timeago.js";
import { SearchItem as SearchItemType } from "frontend/boards-grid/search-section/types";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import styles from "./search-item.module.css";
import { searchCustomLocale } from "utils/date-utils";

register("custom-locale", searchCustomLocale);

type SearchItemProps = SearchItemType & {
  onClick: (documentId: string) => void;
  style: CSSProperties;
};

const TOOLTIP_STYLE: CSSProperties = {
  width: "fit-content",
  backgroundColor: "#ebedf3",
  color: "#113357",
  padding: "0 12px",
  boxShadow: "0px 2px 4px 0px #00000026",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  font: "400 16px Poppins",
  minHeight: 36,
  transform: "translate(150px)",
  maxWidth: "500px",
  wordBreak: "break-all",
  whiteSpace: "normal",
};

export const SearchItem = ({ documentId, name, breadcrumbs, lastUsed, onClick, style }: SearchItemProps) => {
  const [tooltip, setTooltip] = useState<string | null>(null);
  const nameRef = useRef<HTMLDivElement | null>(null);

  const onItemClick = () => {
    onClick(documentId);
  };

  return (
    <div className={styles.wrapper} onClick={onItemClick} style={style}>
      <div
        className={styles.boardName}
        ref={nameRef}
        onMouseEnter={() => {
          const isTextOverflowing = nameRef.current && nameRef.current.scrollWidth > nameRef.current.clientWidth;
          isTextOverflowing ? setTooltip(name) : setTooltip(null);
        }}
        onMouseLeave={() => setTooltip(null)}
      >
        {name}
      </div>
      {tooltip && (
        <Tooltip relativeTo={nameRef} side={"bottom"} label={name} customStyle={TOOLTIP_STYLE} withArrow={false} />
      )}
      <div className={styles.details}>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <div className={styles.detail}>Opened {format(lastUsed, "custom-locale")}</div>
      </div>
    </div>
  );
};

const Breadcrumbs = ({ breadcrumbs }: { breadcrumbs: SearchItemType["breadcrumbs"] }) => {
  const breadcrumbsRef = useRef<HTMLDivElement | null>(null);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  if (breadcrumbs.length === 0) {
    return null;
  }

  const breadcrumbsText = breadcrumbs.join(" > ");

  return (
    <>
      <div
        className={styles.breadcrumbs}
        ref={breadcrumbsRef}
        onMouseEnter={() => {
          if (!breadcrumbsRef.current) return;

          const isTextOverflowing =
            breadcrumbsRef.current && breadcrumbsRef.current.scrollWidth > breadcrumbsRef.current.clientWidth;
          isTextOverflowing ? setShowTooltip(true) : setShowTooltip(false);
        }}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {breadcrumbsText}
      </div>

      {showTooltip && (
        <Tooltip
          relativeTo={breadcrumbsRef}
          side={"bottom"}
          label={breadcrumbsText}
          customStyle={TOOLTIP_STYLE}
          withArrow={false}
        />
      )}
      <span className={styles.divider} />
    </>
  );
};
